import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { AlertCircle, CheckCircle2, Loader2, Headphones, Eye, EyeOff } from 'lucide-react';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenValid(false);
      return;
    }
    axios
      .get(`${API_URL}/api/auth/verify-reset-token?token=${token}`)
      .then(() => setTokenValid(true))
      .catch(() => setTokenValid(false))
      .finally(() => setVerifying(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('La password deve essere di almeno 8 caratteri.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Le password non corrispondono.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Errore durante il reset della password. Riprova.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-up">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#002FA7] text-white mb-4">
            <Headphones className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            IT Helpdesk
          </h1>
          <p className="text-[#71717A] mt-2">Piattaforma di Assistenza Intelligente</p>
        </div>

        <Card className="border border-[#E4E4E7] shadow-none">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Nuova password
            </CardTitle>
            <CardDescription>
              Scegli una nuova password sicura per il tuo account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {verifying ? (
              <div className="flex items-center justify-center py-8 gap-3 text-[#71717A]">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Verifica del link in corso...</span>
              </div>
            ) : !tokenValid ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <AlertCircle className="w-12 h-12 text-[#DC2626]" />
                </div>
                <p className="text-[#DC2626] font-medium">Link non valido o scaduto</p>
                <p className="text-sm text-[#71717A]">
                  Il link di reset è scaduto o non è valido. Richiedi un nuovo link.
                </p>
                <Link
                  to="/forgot-password"
                  className="inline-block text-sm text-[#002FA7] hover:underline font-medium"
                >
                  Richiedi nuovo link
                </Link>
              </div>
            ) : success ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <CheckCircle2 className="w-12 h-12 text-green-500" />
                </div>
                <p className="text-[#0F172A] font-medium">Password aggiornata!</p>
                <p className="text-sm text-[#71717A]">
                  La tua password è stata reimpostata con successo. Verrai reindirizzato al login tra pochi secondi.
                </p>
                <Link
                  to="/login"
                  className="inline-block text-sm text-[#002FA7] hover:underline font-medium"
                >
                  Vai al login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password">Nuova Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Almeno 8 caratteri"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoFocus
                      className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7] pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#002FA7]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Conferma Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Ripeti la password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7] pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#002FA7]"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 text-white font-medium"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    'Salva nuova password'
                  )}
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm text-[#71717A] hover:text-[#002FA7]"
                  >
                    Torna al login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-[#71717A] mt-6">
          Powered by IKONET Solutions
        </p>
      </div>
    </div>
  );
}
